// ─── MotoristasView — categoria do motorista (Casais) ────────────────────
// Lista os motoristas do EcooGPS (sync noturno) e permite definir a CATEGORIA
// (campo aditivo local, keyed a externalId; sobrevive ao sync). A categoria
// serve para filtrar os motoristas disponíveis na atribuição.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Tooltip, CircularProgress, Chip, Button, MenuItem, Menu, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { usePlannerTheme, usePlannerDialogTheme, usePlannerToolbarTheme, brandGradient, ON_BRAND } from '../../theme/plannerTheme';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PersonIcon from '@material-ui/icons/Person';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import PhoneIcon from '@material-ui/icons/Phone';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ContactlessIcon from '@material-ui/icons/Contactless';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { setActiveView } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';

// Paleta para novas categorias (ciclo ao criar).
const CAT_PALETTE = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b'];

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surf-page, #f8fafc)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', background: 'var(--surf-card, #fff)', borderBottom: '1px solid var(--surf-border, #e2e8f0)' },
  title: { fontSize: '1.05rem', fontWeight: 800, color: 'var(--surf-title, #0f172a)', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: 'var(--surf-muted, #64748b)', marginTop: 2 },
  toolbar: { display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center', borderBottom: '1px solid var(--surf-border-soft, #f1f5f9)', background: 'var(--surf-card, #fff)' },
  searchInput: { flex: 1, maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: 8, background: 'var(--surf-input-bg, #fff)', fontSize: '0.78rem', color: 'var(--surf-strong, #0f172a)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--surf-input-border, #e2e8f0)' } },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  headerIcon: { width: 34, height: 34, borderRadius: 10, background: 'var(--surf-accent-soft, #eef2ff)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  row: { background: 'var(--surf-card, #fff)', borderRadius: 12, padding: '11px 16px', marginBottom: 9, border: '1px solid var(--surf-border, #e8edf3)', display: 'flex', alignItems: 'center', gap: 14, transition: 'border-color 0.15s, box-shadow 0.15s', '&:hover': { borderColor: 'var(--surf-accent-border, #c7d2fe)', boxShadow: '0 4px 14px rgba(79,70,229,0.08)' } },
  avatar: { width: 38, height: 38, borderRadius: '50%', background: 'var(--surf-accent-soft, #eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  name: { fontSize: '0.88rem', fontWeight: 700, color: 'var(--surf-strong, #0f172a)' },
  meta: { fontSize: '0.68rem', color: 'var(--surf-muted2, #94a3b8)', marginTop: 1 },
  empty: { padding: '80px 24px', textAlign: 'center', color: 'var(--surf-muted2, #94a3b8)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
}));

function MotoristasView({ lang = 'pt' }) {
  const classes = useStyles();
  const theme = usePlannerTheme();
  const newUI = usePlannerToolbarTheme() !== 'classic'; // identidade ecooGPS só em Claro/Escuro
  const grad = brandGradient(theme);
  const dlgTheme = usePlannerDialogTheme();
  const isDark = theme === 'dark';
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  // "Partilhar app" — link da página de instalação (/mobile/) p/ enviar aos
  // motoristas (copiar / WhatsApp / email com a mensagem pronta).
  const [shareAnchor, setShareAnchor] = useState(null);
  const [copied, setCopied] = useState(false);
  const appLink = `${window.location.origin}/mobile/`;
  const appMsg = pt
    ? `Para instalar a app EcooGPS do motorista, abra este link no telemóvel Android:\n${appLink}\n\nToque em "Instalar app" e siga os 3 passos. (Se já tinha uma versão de teste instalada, desinstale-a primeiro.)`
    : `To install the EcooGPS driver app, open this link on your Android phone:\n${appLink}\n\nTap "Install app" and follow the 3 steps.`;
  function copyAppLink() { try { navigator.clipboard.writeText(appLink); setCopied(true); } catch { /* noop */ } setShareAnchor(null); }
  function shareWhatsApp() { window.open(`https://wa.me/?text=${encodeURIComponent(appMsg)}`, '_blank', 'noopener'); setShareAnchor(null); }
  function shareEmail() { window.location.href = `mailto:?subject=${encodeURIComponent(pt ? 'Instalação da app EcooGPS (motorista)' : 'EcooGPS driver app install')}&body=${encodeURIComponent(appMsg)}`; setShareAnchor(null); }

  const [drivers, setDrivers] = useState([]);
  const [categories, setCategories] = useState([]);   // DriverCategory[] do cliente
  const [manageCats, setManageCats] = useState(false); // diálogo "Gerir categorias"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({});     // key → categoria em edição
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);

  // Diálogo de edição completa (telemóvel, cartão de tacógrafo, RFID, …).
  const [editDriver, setEditDriver] = useState(null);   // motorista em edição ou null
  const [form, setForm] = useState({});                 // valores do formulário
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const keyOf = (d) => String(d.externalId || d.tachoCard || d.name || d.id);

  function openCreate() {
    // Criar motorista LOCAL (do planner). Na variante mercadoria este é o único
    // sítio para o fazer — o atalho das Ordens está escondido de propósito.
    setEditDriver({ __new: true, id: null, externalId: null, name: '' });
    setForm({ name: '', phone: '', tachoCard: '', rfid: '', licenseClass: '', email: '', category: '' });
    setEditError(null);
  }

  function openEdit(d) {
    setEditDriver(d);
    setForm({
      name: d.name || '',
      phone: d.phone || '',
      tachoCard: d.tachoCard || '',
      rfid: d.rfid || '',
      licenseClass: d.licenseClass || '',
      email: d.email || '',
      category: d.category || '',
    });
    setEditError(null);
  }
  function closeEdit() { if (!savingEdit) { setEditDriver(null); setEditError(null); } }
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function saveEdit() {
    if (!editDriver) return;
    const d = editDriver;
    // O nome só é editável em motoristas LOCAIS (criados no planner, sem
    // externalId). Nesses usamos o PUT por id (que grava o nome); nos do
    // EcooGPS continuamos no save-fields (merge-safe, sem tocar no nome).
    const isLocalDriver = !d.externalId && !!d.id;
    const newName = (form.name || '').trim();
    setSavingEdit(true); setEditError(null);
    try {
      let r;
      if (d.__new) {
        if (!newName || newName.length < 2) { setEditError(pt ? 'O nome é obrigatório.' : 'Name is required.'); setSavingEdit(false); return; }
        r = await fetch('/planner-api/drivers', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newName,
            phone: form.phone, tachoCard: form.tachoCard, rfid: form.rfid,
            licenseClass: form.licenseClass, email: form.email, category: form.category,
          }),
        });
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
        const created = await r.json().catch(() => null);
        if (created && created.id) {
          setDrivers((prev) => [{ ...created, isLocal: true }, ...prev]);
        } else {
          await fetchAll();
        }
        setEditDriver(null);
        setSavingEdit(false);
        return;
      }
      if (isLocalDriver) {
        if (!newName) { setEditError(pt ? 'O nome é obrigatório.' : 'Name is required.'); setSavingEdit(false); return; }
        r = await fetch(`/planner-api/drivers/${d.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newName,
            phone: form.phone, tachoCard: form.tachoCard, rfid: form.rfid,
            licenseClass: form.licenseClass, email: form.email, category: form.category,
          }),
        });
      } else {
        r = await fetch('/planner-api/drivers/save-fields', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            externalId: d.externalId || null, name: d.name,
            phone: form.phone, tachoCard: form.tachoCard, rfid: form.rfid,
            licenseClass: form.licenseClass, email: form.email, category: form.category,
          }),
        });
      }
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const k = keyOf(d);
      setDrivers((prev) => prev.map((x) => keyOf(x) === k ? {
        ...x, name: isLocalDriver ? newName : x.name,
        phone: form.phone || null, tachoCard: form.tachoCard || null, rfid: form.rfid || null,
        licenseClass: form.licenseClass || null, email: form.email || null, category: form.category || null,
      } : x));
      setEditDriver(null);
    } catch (e) {
      setEditError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally { setSavingEdit(false); }
  }

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch('/planner-api/drivers', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setDrivers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(pt ? 'Não foi possível carregar os motoristas.' : 'Could not load drivers.');
    } finally { setLoading(false); }
  }, [token, pt]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/planner-api/driver-categories', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const data = await r.json(); setCategories(Array.isArray(data) ? data : []); }
    } catch { /* silencioso — a lista fica vazia */ }
  }, [token]);

  useEffect(() => { fetchAll(); fetchCategories(); }, [fetchAll, fetchCategories]);

  const catNames = categories.map(c => c.name);

  async function saveCategory(d, override) {
    const k = keyOf(d);
    const value = override !== undefined ? override : (edits[k] !== undefined ? edits[k] : (d.category || ''));
    if ((d.category || '') === value) return; // nada mudou
    setSavingKey(k); setError(null);
    try {
      const r = await fetch('/planner-api/drivers/set-category', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ externalId: d.externalId || null, tachoCard: d.tachoCard || null, name: d.name, category: value }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setDrivers(prev => prev.map(x => keyOf(x) === k ? { ...x, category: value || null } : x));
      setEdits(prev => { const n = { ...prev }; delete n[k]; return n; });
      setSavedKey(k); setTimeout(() => setSavedKey(cur => cur === k ? null : cur), 1500);
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally { setSavingKey(null); }
  }

  const filtered = drivers.filter(d => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [d.name, d.category, d.tachoCard, d.rfid, d.phone].some(v => v && String(v).toLowerCase().includes(q));
  });

  return (
    <ThemeProvider theme={dlgTheme}>
    <Box className={classes.root} data-planner-theme={theme}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}><ArrowBackIcon style={{ fontSize: 20 }} /></IconButton>
        </Tooltip>
        <Box style={{ flex: 1 }}>
          <Typography className={classes.title}><Box component="span" className={classes.headerIcon} style={newUI ? { background: grad, boxShadow: '0 6px 16px rgba(24,189,238,0.34)' } : undefined}><PersonIcon style={{ fontSize: 19, color: newUI ? ON_BRAND : '#4f46e5' }} /></Box> {pt ? 'Motoristas' : 'Drivers'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Motoristas do EcooGPS — definir categoria (usada para filtrar disponíveis)' : 'EcooGPS drivers — set category'}</Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}><IconButton size="small" onClick={fetchAll} disabled={loading}><RefreshIcon style={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField className={classes.searchInput} size="small" variant="outlined"
          placeholder={pt ? 'Procurar motorista ou categoria…' : 'Search…'} value={search} onChange={e => setSearch(e.target.value)} />
        <Box style={{ flex: 1 }} />
        <Button size="small" variant="contained" disableElevation startIcon={<AddIcon style={{ fontSize: 16 }} />}
          onClick={openCreate}
          style={{ textTransform: 'none', fontSize: '0.74rem', background: newUI ? grad : '#4f46e5', color: newUI ? ON_BRAND : '#fff', fontWeight: 700 }}>
          {pt ? 'Novo motorista' : 'New driver'}
        </Button>
        <Button size="small" variant="outlined" startIcon={<LocalOfferIcon style={{ fontSize: 16 }} />}
          onClick={() => setManageCats(true)}
          style={{ textTransform: 'none', fontSize: '0.74rem', borderColor: '#c7d2fe', color: '#4f46e5', fontWeight: 700 }}>
          {pt ? 'Gerir categorias' : 'Manage categories'}
        </Button>
        <Button size="small" variant="outlined" startIcon={<ShareIcon style={{ fontSize: 16 }} />}
          onClick={(e) => setShareAnchor(e.currentTarget)}
          title={pt ? 'Enviar aos motoristas o link para instalar a app' : 'Send drivers the app install link'}
          style={{ textTransform: 'none', fontSize: '0.74rem', borderColor: newUI ? 'rgba(24,189,238,0.5)' : '#86efac', color: newUI ? '#0a86b8' : '#16a34a', fontWeight: 700 }}>
          {pt ? 'Partilhar app' : 'Share app'}
        </Button>
        <Menu anchorEl={shareAnchor} open={!!shareAnchor} onClose={() => setShareAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Box style={{ padding: '6px 14px 8px', maxWidth: 260 }}>
            <Typography style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{pt ? 'Link de instalação' : 'Install link'}</Typography>
            <Typography style={{ fontSize: '0.72rem', color: '#2563eb', wordBreak: 'break-all', marginTop: 2 }}>{appLink}</Typography>
          </Box>
          <MenuItem onClick={copyAppLink} style={{ fontSize: '0.82rem', gap: 10 }}>📋 {pt ? 'Copiar link' : 'Copy link'}</MenuItem>
          <MenuItem onClick={shareWhatsApp} style={{ fontSize: '0.82rem', gap: 10 }}>🟢 {pt ? 'Enviar por WhatsApp' : 'Send via WhatsApp'}</MenuItem>
          <MenuItem onClick={shareEmail} style={{ fontSize: '0.82rem', gap: 10 }}>✉️ {pt ? 'Enviar por email' : 'Send via email'}</MenuItem>
        </Menu>
        <Chip label={`${drivers.length} ${pt ? 'motoristas' : 'drivers'}`} size="small" style={{ background: '#eef2ff', color: '#4f46e5', fontWeight: 700 }} />
      </Box>
      <Snackbar open={copied} autoHideDuration={2500} onClose={() => setCopied(false)}
        message={pt ? 'Link copiado ✓' : 'Link copied ✓'} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

      <Box className={classes.list}>
        {error && <Box style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem' }}>{error}</Box>}
        {loading && drivers.length === 0 ? (
          <Box className={classes.empty}><CircularProgress size={32} style={{ color: '#4f46e5' }} /></Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            {newUI
              ? <Box style={{ width: 66, height: 66, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf-head-bg, #f1f6f9)', color: theme === 'dark' ? '#6f93a6' : '#94a3b8' }}><PersonIcon style={{ fontSize: 32 }} /></Box>
              : <PersonIcon style={{ fontSize: 48, color: '#cbd5e1' }} />}
            <Typography style={{ fontWeight: 700, color: 'var(--surf-title, #0f172a)' }}>{pt ? 'Sem motoristas.' : 'No drivers.'}</Typography>
          </Box>
        ) : filtered.map(d => {
          const k = keyOf(d);
          const val = edits[k] !== undefined ? edits[k] : (d.category || '');
          const dirty = (d.category || '') !== val;
          return (
            <Box key={k} className={classes.row}>
              <Box className={classes.avatar}><PersonIcon style={{ fontSize: 18, color: '#4f46e5' }} /></Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.name}>{d.name}</Typography>
                <Typography className={classes.meta}>
                  {[
                    d.tachoCard ? `${pt ? 'Tacógrafo' : 'Tacho'} ${d.tachoCard}` : null,
                    d.rfid ? `RFID ${d.rfid}` : null,
                    d.phone || null,
                    (!d.tachoCard && !d.rfid && !d.phone) ? (d.externalId ? `EcooGPS #${d.externalId}` : (pt ? 'Local' : 'Local')) : null,
                  ].filter(Boolean).join(' · ')}
                  {d.isLocal === false ? ' · EcooGPS' : ''}
                </Typography>
              </Box>
              <TextField
                select
                size="small" variant="outlined"
                label={pt ? 'Categoria' : 'Category'}
                value={val}
                onChange={e => { const nv = e.target.value; setEdits(prev => ({ ...prev, [k]: nv })); saveCategory(d, nv); }}
                style={{ width: 240 }}
              >
                <MenuItem value=""><em>{pt ? '— sem categoria' : '— none'}</em></MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id} value={c.name}>
                    <Box component="span" style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: c.color, marginRight: 8, verticalAlign: 'middle' }} />
                    {c.name}
                  </MenuItem>
                ))}
                {val && !catNames.includes(val) && <MenuItem value={val}>{val}</MenuItem>}
              </TextField>
              {savingKey === k ? <CircularProgress size={18} />
                : savedKey === k ? <CheckIcon style={{ fontSize: 20, color: '#16a34a' }} />
                : dirty ? (
                  <Button size="small" onClick={() => saveCategory(d)} style={{ textTransform: 'none', fontSize: '0.72rem', color: '#4f46e5' }}>{pt ? 'Guardar' : 'Save'}</Button>
                ) : <Box style={{ width: 24 }} />}
              <Tooltip title={pt ? 'Editar motorista' : 'Edit driver'}>
                <IconButton size="small" onClick={() => openEdit(d)}><EditIcon style={{ fontSize: 18, color: '#64748b' }} /></IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <Dialog open={!!editDriver} onClose={closeEdit} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: 16, backgroundImage: 'none' } }}>
        <DialogTitle style={{ paddingBottom: 4 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Box className={classes.avatar}><PersonIcon style={{ fontSize: 18, color: '#4f46e5' }} /></Box>
            <Box style={{ minWidth: 0 }}>
              <Typography style={{ fontSize: '1rem', fontWeight: 800, color: isDark ? '#eaf3fb' : '#0f172a', lineHeight: 1.2 }}>
                {editDriver?.__new ? (pt ? 'Novo motorista' : 'New driver') : editDriver?.name}
              </Typography>
              <Typography style={{ fontSize: '0.68rem', color: isDark ? '#9fb3c8' : '#94a3b8' }}>
                {editDriver?.__new
                  ? (pt ? 'Motorista do planner' : 'Planner driver')
                  : editDriver?.externalId ? `EcooGPS #${editDriver.externalId}` : (pt ? 'Motorista local' : 'Local driver')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
          {editError && (
            <Box style={{ padding: '9px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, fontSize: '0.76rem' }}>{editError}</Box>
          )}
          <TextField size="small" variant="outlined" fullWidth label={pt ? 'Nome' : 'Name'}
            value={form.name || ''} onChange={setField('name')}
            disabled={!editDriver?.__new && !!editDriver?.externalId}
            autoFocus={!!editDriver?.__new}
            helperText={editDriver?.externalId && !editDriver?.__new
              ? (pt ? 'Nome gerido no EcooGPS — só se altera lá.' : 'Managed in EcooGPS.')
              : (pt ? 'Motorista do planner — nome editável.' : 'Planner driver — editable name.')}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
          <TextField size="small" variant="outlined" fullWidth label={pt ? 'Telemóvel' : 'Phone'}
            value={form.phone || ''} onChange={setField('phone')}
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
          <TextField size="small" variant="outlined" fullWidth label={pt ? 'Cartão de tacógrafo' : 'Tachograph card'}
            value={form.tachoCard || ''} onChange={setField('tachoCard')}
            InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
          <TextField size="small" variant="outlined" fullWidth label="RFID"
            value={form.rfid || ''} onChange={setField('rfid')}
            InputProps={{ startAdornment: <InputAdornment position="start"><ContactlessIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
          <TextField size="small" variant="outlined" fullWidth label={pt ? 'Carta de condução' : 'License class'}
            value={form.licenseClass || ''} onChange={setField('licenseClass')} />
          <TextField size="small" variant="outlined" fullWidth label="Email" type="email"
            value={form.email || ''} onChange={setField('email')} />
          <TextField select size="small" variant="outlined" fullWidth label={pt ? 'Categoria' : 'Category'}
            value={form.category || ''} onChange={setField('category')}>
            <MenuItem value=""><em>{pt ? '— sem categoria' : '— none'}</em></MenuItem>
            {categories.map(c => (
              <MenuItem key={c.id} value={c.name}>
                <Box component="span" style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: c.color, marginRight: 8, verticalAlign: 'middle' }} />
                {c.name}
              </MenuItem>
            ))}
            {form.category && !catNames.includes(form.category) && <MenuItem value={form.category}>{form.category}</MenuItem>}
          </TextField>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={closeEdit} disabled={savingEdit} style={{ textTransform: 'none', color: '#64748b' }}>{pt ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={saveEdit} disabled={savingEdit} variant="contained" disableElevation
            style={{ textTransform: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700 }}
            startIcon={savingEdit ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 18 }} />}>
            {editDriver?.__new ? (pt ? 'Criar' : 'Create') : (pt ? 'Guardar' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryManagerDialog
        open={manageCats} onClose={() => setManageCats(false)}
        token={token} pt={pt} categories={categories} onChanged={fetchCategories} isDark={isDark}
      />
    </Box>
    </ThemeProvider>
  );
}

// ─── Gestor de categorias de motorista (por cliente) ─────────────────────
function CategoryManagerDialog({ open, onClose, token, pt, categories, onChanged, isDark }) {
  const [newName, setNewName] = useState('');
  const [nameEdits, setNameEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function api(method, path, body) {
    const r = await fetch(`/planner-api/driver-categories${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
    return r.json().catch(() => ({}));
  }
  async function run(fn) { setBusy(true); setErr(null); try { await fn(); await onChanged(); } catch (e) { setErr(e.message || 'Erro'); } finally { setBusy(false); } }

  const add = () => { const name = newName.trim(); if (!name) return; run(async () => { await api('POST', '', { name, color: CAT_PALETTE[categories.length % CAT_PALETTE.length] }); setNewName(''); }); };
  const rename = (c) => { const name = (nameEdits[c.id] ?? c.name).trim(); if (!name || name === c.name) return; run(async () => { await api('PUT', `/${c.id}`, { name }); setNameEdits(p => { const n = { ...p }; delete n[c.id]; return n; }); }); };
  const cycleColor = (c) => { const idx = CAT_PALETTE.indexOf(c.color); const color = CAT_PALETTE[(idx + 1) % CAT_PALETTE.length]; run(() => api('PUT', `/${c.id}`, { color })); };
  const remove = (c) => { if (!window.confirm(pt ? `Remover a categoria "${c.name}"? Os motoristas que a tinham mantêm o texto; ela só deixa de aparecer na lista.` : `Remove "${c.name}"?`)) return; run(() => api('DELETE', `/${c.id}`)); };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16, backgroundImage: 'none' } }}>
      <DialogTitle>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LocalOfferIcon style={{ fontSize: 20, color: '#4f46e5' }} />
          <Typography style={{ fontSize: '1rem', fontWeight: 800, color: isDark ? '#eaf3fb' : '#0f172a' }}>{pt ? 'Categorias de motorista' : 'Driver categories'}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
        {err && <Box style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, fontSize: '0.75rem' }}>{err}</Box>}
        {categories.length === 0 && <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{pt ? 'Ainda não há categorias. Cria a primeira abaixo.' : 'No categories yet.'}</Typography>}
        {categories.map(c => {
          const val = nameEdits[c.id] ?? c.name;
          const dirty = val.trim() !== c.name;
          return (
            <Box key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tooltip title={pt ? 'Mudar cor' : 'Change color'}>
                <IconButton size="small" onClick={() => cycleColor(c)} disabled={busy} style={{ padding: 4 }}>
                  <Box style={{ width: 16, height: 16, borderRadius: '50%', background: c.color, border: '1px solid #e2e8f0' }} />
                </IconButton>
              </Tooltip>
              <TextField size="small" variant="outlined" value={val} disabled={busy}
                onChange={e => setNameEdits(p => ({ ...p, [c.id]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') rename(c); }}
                style={{ flex: 1 }} />
              {dirty && <Button size="small" onClick={() => rename(c)} disabled={busy} style={{ textTransform: 'none', minWidth: 0, color: '#4f46e5' }}><CheckIcon style={{ fontSize: 18 }} /></Button>}
              <Tooltip title={pt ? 'Remover' : 'Remove'}>
                <IconButton size="small" onClick={() => remove(c)} disabled={busy} style={{ padding: 4 }}><DeleteOutlineIcon style={{ fontSize: 18, color: '#ef4444' }} /></IconButton>
              </Tooltip>
            </Box>
          );
        })}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
          <TextField size="small" variant="outlined" placeholder={pt ? 'Nova categoria…' : 'New category…'}
            value={newName} disabled={busy} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }} style={{ flex: 1 }} />
          <Button size="small" variant="contained" disableElevation onClick={add} disabled={busy || !newName.trim()}
            startIcon={busy ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <AddIcon style={{ fontSize: 18 }} />}
            style={{ textTransform: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700 }}>
            {pt ? 'Adicionar' : 'Add'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '10px 20px' }}>
        <Button onClick={onClose} disabled={busy} style={{ textTransform: 'none', color: '#64748b' }}>{pt ? 'Fechar' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default MotoristasView;
